import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSocket } from '../../context/SocketContext';
import { StorageDisplay } from './StorageDisplay';
import { CreateGroupModal } from './CreateGroupModal';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const { isAdmin } = useAuth();
  const { bandmates, totalUnreadChat, onlineUsers, openDM, activeConversationId, dmConversationMap, groupConversations, openConversation } = useSocket();
  const [bandmatesExpanded, setBandmatesExpanded] = useState(true);
  const [createGroupProjectId, setCreateGroupProjectId] = useState<string | null>(null);

  const navItems = [
    { path: '/', label: 'Dashboard', icon: 'home' },
    { path: '/projects', label: 'Projects', icon: 'folder' },
    { path: '/files', label: 'Files', icon: 'files' },
  ];

  const adminItems = [{ path: '/admin/users', label: 'Users', icon: 'users' }];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const renderIcon = (icon: string) => {
    switch (icon) {
      case 'home':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
            />
          </svg>
        );
      case 'folder':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
            />
          </svg>
        );
      case 'users':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        );
      case 'files':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  const renderAvatar = (name: string, avatarUrl?: string | null, isOnline?: boolean) => {
    const initials = name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
    return (
      <div className="relative flex-shrink-0">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={name}
            referrerPolicy="no-referrer"
            className="w-7 h-7 rounded-full object-cover"
          />
        ) : (
          <div className="w-7 h-7 rounded-full bg-primary/15 text-primary/70 flex items-center justify-center text-[10px] font-semibold">
            {initials}
          </div>
        )}
        {isOnline !== undefined && (
          <span
            className={`absolute bottom-0 right-0 w-2 h-2 rounded-full border border-surface ${
              isOnline ? 'bg-green-500' : 'bg-zinc-400'
            }`}
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden touch-none"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-surface border-r border-border z-50 transform transition-transform duration-300 ease-in-out lg:translate-x-0 flex flex-col will-change-transform ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border flex-shrink-0">
          <Link to="/" className="flex items-center gap-2" onClick={onClose}>
            <img 
              src="/logo.png" 
              alt="BandMate" 
              className="w-8 h-8 rounded object-contain"
            />
            <span className="text-xl font-bold text-primary italic">BandMate</span>
          </Link>
          <button
            onClick={onClose}
            className="p-1 text-muted hover:text-text lg:hidden"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Scrollable middle section */}
        <div className="flex-1 overflow-y-auto min-h-0">

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                isActive(item.path)
                  ? 'bg-primary/15 text-primary'
                  : 'text-muted hover:text-text hover:bg-surface-light'
              }`}
            >
              {renderIcon(item.icon)}
              <span className="font-medium flex-1">{item.label}</span>
            </Link>
          ))}

          {isAdmin && (
            <>
              <div className="pt-4 pb-2">
                <span className="px-3 text-xs font-semibold text-muted uppercase tracking-wider">
                  Admin
                </span>
              </div>
              {adminItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary/15 text-primary'
                      : 'text-muted hover:text-text hover:bg-surface-light'
                  }`}
                >
                  {renderIcon(item.icon)}
                  <span className="font-medium">{item.label}</span>
                </Link>
              ))}
            </>
          )}
        </nav>

        {/* BandMates — chat section (desktop only; mobile uses FAB + bottom sheet) */}
        {bandmates && bandmates.projects.length > 0 && (
          <div className="hidden lg:block px-4 pb-2">
            <button
              onClick={() => setBandmatesExpanded((v) => !v)}
              className="w-full flex items-center justify-between py-2 text-xs font-semibold text-muted uppercase tracking-wider hover:text-text transition-colors"
            >
              <div className="flex items-center gap-1.5">
                <span>BandMates</span>
                {totalUnreadChat > 0 && (
                  <span className="bg-primary/70 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center leading-none">
                    {totalUnreadChat > 99 ? '99+' : totalUnreadChat}
                  </span>
                )}
              </div>
              <svg
                className={`w-3 h-3 transition-transform ${bandmatesExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {bandmatesExpanded && (
              <div className="space-y-3 mt-1">
                {bandmates.projects.map((project) => (
                  <div key={project.id}>
                    <div className="flex items-center justify-between px-1 mb-1 group">
                      <p className="text-[11px] font-semibold text-muted truncate" title={project.name}>
                        {project.name}
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCreateGroupProjectId(project.id);
                        }}
                        className="p-0.5 rounded text-muted/50 hover:text-primary hover:bg-primary/10 transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
                        title="New group chat"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                    <div className="space-y-0.5 ml-2 border-l border-border/50 pl-2">
                      {project.members.map((member) => {
                        const isOnline = onlineUsers.has(member.id) || member.isOnline;
                        const isActive = dmConversationMap[member.id] === activeConversationId;
                        return (
                          <button
                            key={`${project.id}-${member.id}`}
                            onClick={() => openDM(member.id)}
                            className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg transition-colors text-left ${
                              isActive
                                ? 'bg-primary/15 text-primary'
                                : 'text-muted hover:text-text hover:bg-surface-light'
                            }`}
                          >
                            {renderAvatar(member.name, member.avatarUrl, isOnline)}
                            <span className="text-sm font-medium truncate flex-1">{member.name}</span>
                            {member.unreadCount > 0 && (
                              <span className="bg-primary/70 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center leading-none flex-shrink-0">
                                {member.unreadCount > 99 ? '99+' : member.unreadCount}
                              </span>
                            )}
                          </button>
                        );
                      })}
                      {/* Group conversations for this project */}
                    {groupConversations
                      .filter((c) => c.projectId === project.id)
                      .map((conv) => {
                        const isActive = conv.id === activeConversationId;
                        return (
                          <button
                            key={conv.id}
                            onClick={() => openConversation(conv.id)}
                            className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg transition-colors text-left ${
                              isActive
                                ? 'bg-primary/15 text-primary'
                                : 'text-muted hover:text-text hover:bg-surface-light'
                            }`}
                          >
                            {/* Group icon */}
                            <div className="relative flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary/70 flex items-center justify-center">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium truncate block">{conv.name || 'Group Chat'}</span>
                              <span className="text-[10px] text-muted/60">{conv.members.length} members</span>
                            </div>
                            {conv.unreadCount > 0 && (
                              <span className="bg-primary/70 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center leading-none flex-shrink-0">
                                {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        </div>

        {/* Storage Display - at bottom of sidebar */}
        <StorageDisplay />

        {/* Support / Docs links */}
        <div className="p-4 border-t border-border flex flex-col gap-2">
          <a
            href="https://grimothy.github.io/BandMate_site/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-muted hover:text-primary hover:bg-primary/10 transition-colors text-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C6.5 6.75 3 10 3 13c0 3 3 6.25 9 6.75m0-13c5.5-.5 9 3.75 9 6.75 0 3-3 6-9 6.75" />
            </svg>
            <span>Documentation</span>
          </a>

          <a
            href="https://ko-fi.com/mrgrimothy"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-muted hover:text-primary hover:bg-primary/10 transition-colors text-sm"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
            <span>Support BandMate</span>
          </a>
        </div>


      </aside>

      {/* Group chat creation modal */}
      {bandmates && createGroupProjectId && (() => {
        const project = bandmates.projects.find((p) => p.id === createGroupProjectId);
        if (!project) return null;
        return (
          <CreateGroupModal
            isOpen
            onClose={() => setCreateGroupProjectId(null)}
            projectId={project.id}
            projectName={project.name}
            members={project.members}
          />
        );
      })()}
    </>
  );
}
